import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "../../lib/utils";

const Tabs = TabsPrimitive.Root;

// The underline variant (NO-791) renders exactly like the default segmented
// control until the always theme is on: every utility that differs reads a
// value only [data-theme="always"] defines, with the classic value as its
// fallback. Under the theme the list loses its fill and each trigger is plain
// text with a 2px plum underline when active. TabsList sets the variant;
// its triggers read it from context.
export type TabsVariant = "default" | "underline";

const TabsVariantContext = React.createContext<TabsVariant>("default");

const UNDERLINE_LIST =
  "h-(--tabs-underline-list-height,2.25rem) gap-(--tabs-underline-gap,0px) rounded-(--tabs-underline-radius,var(--radius-lg)) bg-(--tabs-underline-list-bg,var(--muted)) p-(--tabs-underline-list-padding,0.25rem)";

const UNDERLINE_TRIGGER =
  "rounded-(--tabs-underline-radius,var(--radius-md)) px-(--tabs-underline-trigger-px,0.75rem) py-(--tabs-underline-trigger-py,0.25rem) border-b-(length:--tabs-underline-edge,0px) border-b-transparent font-(family-name:--tabs-underline-font) font-(weight:--tabs-underline-weight,500) data-[state=active]:border-b-(color:--tabs-underline-active-edge,transparent) data-[state=active]:bg-(--tabs-underline-active-bg,var(--background)) data-[state=active]:shadow-(--tabs-underline-active-shadow,0_1px_3px_rgba(40,3,15,0.07),0_1px_2px_rgba(40,3,15,0.05))";

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: TabsVariant }
>(({ className, variant = "default", ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
        variant === "underline" ? UNDERLINE_LIST : undefined,
        className
      )}
      {...props}
    />
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        variant === "underline" ? UNDERLINE_TRIGGER : undefined,
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
